import { useState, useEffect } from 'react';
import { Plus, Search, Users, BookOpen, GraduationCap, Trash2, Edit, ChevronRight, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api, { levelApi, classApi, schoolYearApi, studentApi, teacherApi } from '../lib/api';

export default function ClassesPage() {
  const [levels, setLevels] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schoolYear, setSchoolYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLevelDialog, setShowLevelDialog] = useState(false);
  const [showClassDialog, setShowClassDialog] = useState(false);
  const [showEditClassDialog, setShowEditClassDialog] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('quranic');
  const [viewMode, setViewMode] = useState('levels'); // 'levels' or 'dashboard'

  const [newLevel, setNewLevel] = useState({ name: '', type: 'quranic', order: 0, description: '' });
  const [newClass, setNewClass] = useState({ name: '', level_id: '', capacity: 30, teacher_id: '' });

  useEffect(() => { fetchData(); }, [selectedType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [levelsRes, classesRes, yearRes, studentsRes, teachersRes] = await Promise.all([
        levelApi.getAll(selectedType),
        classApi.getAll({}),
        schoolYearApi.getActive(),
        studentApi.getAll({ is_active: true }),
        teacherApi.getAll({ is_active: true })
      ]);
      setLevels(levelsRes.data);
      setClasses(classesRes.data);
      setSchoolYear(yearRes.data);
      setStudents(studentsRes.data);
      setTeachers(teachersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLevel = async (e) => {
    e.preventDefault();
    if (!newLevel.name) {
      toast.error('Nom du niveau requis');
      return;
    }
    setFormLoading(true);
    try {
      await levelApi.create({ ...newLevel, type: selectedType });
      toast.success('Niveau créé');
      setShowLevelDialog(false);
      setNewLevel({ name: '', type: selectedType, order: 0, description: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddClass = async (e) => {
    e.preventDefault();
    if (!newClass.name || !newClass.level_id) {
      toast.error('Nom et niveau requis');
      return;
    }
    setFormLoading(true);
    try {
      await classApi.create({ ...newClass, school_year_id: schoolYear?.id || '' });
      toast.success('Classe créée');
      setShowClassDialog(false);
      setNewClass({ name: '', level_id: '', capacity: 30, teacher_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditClass = async (e) => {
    e.preventDefault();
    if (!selectedClass) return;
    setFormLoading(true);
    try {
      await api.put(`/classes/${selectedClass.id}`, {
        name: selectedClass.name,
        level_id: selectedClass.level_id,
        capacity: selectedClass.capacity,
        teacher_id: selectedClass.teacher_id || null,
        school_year_id: selectedClass.school_year_id
      });
      toast.success('Classe mise à jour');
      setShowEditClassDialog(false);
      setSelectedClass(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditClass = (cls) => {
    setSelectedClass({ ...cls });
    setShowEditClassDialog(true);
  };

  const handleDeleteLevel = async (id) => {
    if (!window.confirm('Supprimer ce niveau ?')) return;
    try {
      await api.delete(`/levels/${id}`);
      toast.success('Niveau supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteClass = async (id) => {
    if (!window.confirm('Supprimer cette classe ?')) return;
    try {
      await api.delete(`/classes/${id}`);
      toast.success('Classe supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getClassesForLevel = (levelId) => classes.filter(c => c.level_id === levelId);

  const getStudentsForClass = (classId) => students.filter(s => s.class_id === classId);

  const getTeacherForClass = (teacherId) => teachers.find(t => t.id === teacherId);

  const getLevelName = (levelId) => levels.find(l => l.id === levelId)?.name || 'N/A';

  const quranicLevels = [
    { name: 'Iqra', description: 'Apprentissage de la lecture' },
    { name: 'Juz Amma', description: '30ème partie du Coran' },
    { name: '5 Juz', description: 'Mémorisation de 5 parties' },
    { name: '10 Juz', description: 'Mémorisation de 10 parties' },
    { name: '15 Juz', description: 'Mémorisation de 15 parties' },
    { name: 'Hafiz', description: 'Mémorisation complète' },
  ];

  const classicalLevels = [
    { name: 'Maternelle PS', description: 'Petite Section' },
    { name: 'Maternelle MS', description: 'Moyenne Section' },
    { name: 'Maternelle GS', description: 'Grande Section' },
    { name: 'CI', description: 'Cours d\'Initiation' },
    { name: 'CP', description: 'Cours Préparatoire' },
    { name: 'CE1', description: 'Cours Élémentaire 1' },
    { name: 'CE2', description: 'Cours Élémentaire 2' },
    { name: 'CM1', description: 'Cours Moyen 1' },
    { name: 'CM2', description: 'Cours Moyen 2' },
  ];

  const seedLevels = async (type) => {
    const levelsList = type === 'quranic' ? quranicLevels : classicalLevels;
    setFormLoading(true);
    try {
      for (let i = 0; i < levelsList.length; i++) {
        await levelApi.create({
          name: levelsList[i].name,
          type: type,
          order: i,
          description: levelsList[i].description
        });
      }
      toast.success(`${levelsList.length} niveaux créés`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="classes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Classes & Niveaux</h1>
          <p className="text-muted-foreground text-sm">Gérez l'organisation pédagogique</p>
        </div>
        <div className="flex gap-2">
          {selectedType === 'classical' && (
            <>
              <Button 
                variant={viewMode === 'levels' ? 'default' : 'outline'} 
                className="rounded-full"
                onClick={() => setViewMode('levels')}
              >
                <BookOpen className="w-4 h-4 mr-2" />Niveaux
              </Button>
              <Button 
                variant={viewMode === 'dashboard' ? 'default' : 'outline'} 
                className="rounded-full"
                onClick={() => setViewMode('dashboard')}
              >
                <Users className="w-4 h-4 mr-2" />Tableau de bord
              </Button>
              <Dialog open={showLevelDialog} onOpenChange={setShowLevelDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="add-level-btn">
                    <Plus className="w-4 h-4 mr-2" />Niveau
                  </Button>
                </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouveau niveau</DialogTitle>
                <DialogDescription>Créer un niveau d'enseignement</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddLevel} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input value={newLevel.name} onChange={(e) => setNewLevel({...newLevel, name: e.target.value})} placeholder="Ex: CE1, 5 Juz..." />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input value={newLevel.description} onChange={(e) => setNewLevel({...newLevel, description: e.target.value})} placeholder="Description du niveau" />
                </div>
                <div className="space-y-2">
                  <Label>Ordre</Label>
                  <Input type="number" value={newLevel.order} onChange={(e) => setNewLevel({...newLevel, order: parseInt(e.target.value) || 0})} />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowLevelDialog(false)}>Annuler</Button>
                  <Button type="submit" disabled={formLoading}>{formLoading ? 'Création...' : 'Créer'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
            </>
          )}

          <Dialog open={showClassDialog} onOpenChange={setShowClassDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-class-btn">
                <Plus className="w-4 h-4 mr-2" />Classe
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouvelle classe</DialogTitle>
                <DialogDescription>Créer une classe pour un niveau</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddClass} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input value={newClass.name} onChange={(e) => setNewClass({...newClass, name: e.target.value})} placeholder="Ex: CE1-A, Juz Amma 1..." />
                </div>
                <div className="space-y-2">
                  <Label>Niveau *</Label>
                  <Select value={newClass.level_id} onValueChange={(v) => setNewClass({...newClass, level_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      {levels.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Professeur</Label>
                  <Select value={newClass.teacher_id || "none"} onValueChange={(v) => setNewClass({...newClass, teacher_id: v === "none" ? "" : v})}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner un professeur" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Capacité</Label>
                  <Input type="number" value={newClass.capacity} onChange={(e) => setNewClass({...newClass, capacity: parseInt(e.target.value) || 30})} />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowClassDialog(false)}>Annuler</Button>
                  <Button type="submit" disabled={formLoading}>{formLoading ? 'Création...' : 'Créer'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Class Dialog */}
          <Dialog open={showEditClassDialog} onOpenChange={setShowEditClassDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Modifier la classe</DialogTitle>
                <DialogDescription>Modifier les informations de la classe</DialogDescription>
              </DialogHeader>
              {selectedClass && (
                <form onSubmit={handleEditClass} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nom *</Label>
                    <Input value={selectedClass.name} onChange={(e) => setSelectedClass({...selectedClass, name: e.target.value})} />
                  </div>
                  {/* Masquer le champ Niveau pour les classes coraniques */}
                  {selectedType === 'classical' && (
                    <div className="space-y-2">
                      <Label>Niveau *</Label>
                      <Select value={selectedClass.level_id} onValueChange={(v) => setSelectedClass({...selectedClass, level_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                        <SelectContent>
                          {levels.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Professeur</Label>
                    <Select value={selectedClass.teacher_id || "none"} onValueChange={(v) => setSelectedClass({...selectedClass, teacher_id: v === "none" ? "" : v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner un professeur" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Capacité</Label>
                    <Input type="number" value={selectedClass.capacity} onChange={(e) => setSelectedClass({...selectedClass, capacity: parseInt(e.target.value) || 30})} />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowEditClassDialog(false)}>Annuler</Button>
                    <Button type="submit" disabled={formLoading}>{formLoading ? 'Mise à jour...' : 'Enregistrer'}</Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={selectedType} onValueChange={setSelectedType}>
        <TabsList>
          <TabsTrigger value="quranic" className="gap-2">
            <BookOpen className="w-4 h-4" />Coranique
          </TabsTrigger>
          <TabsTrigger value="classical" className="gap-2">
            <GraduationCap className="w-4 h-4" />Classique
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedType} className="mt-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : selectedType === 'quranic' ? (
            /* VUE CORANIQUE - Affichage direct des classes sans niveaux */
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-green-600" />
                  Classes Coraniques
                </CardTitle>
                <CardDescription>Les 6 classes d'enseignement coranique</CardDescription>
              </CardHeader>
              <CardContent>
                {classes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Aucune classe créée</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Classe</TableHead>
                        <TableHead>Professeur</TableHead>
                        <TableHead className="text-center">Élèves</TableHead>
                        <TableHead className="text-center">Capacité</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {classes.map((cls) => {
                        const teacher = getTeacherForClass(cls.teacher_id);
                        const classStudents = getStudentsForClass(cls.id);
                        
                        return (
                          <TableRow key={cls.id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                  <BookOpen className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{cls.name}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {teacher ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <User className="w-4 h-4 text-primary" />
                                  </div>
                                  <span>{teacher.first_name} {teacher.last_name}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground italic">Non assigné</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={classStudents.length > 0 ? "default" : "secondary"}>
                                {classStudents.length}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">{cls.capacity}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditClass(cls)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteClass(cls.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : viewMode === 'dashboard' ? (
            /* DASHBOARD VIEW - Tableau des classes avec professeur et élèves */
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Tableau de bord des classes
                </CardTitle>
                <CardDescription>Vue d'ensemble des classes avec professeurs et élèves</CardDescription>
              </CardHeader>
              <CardContent>
                {classes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Aucune classe créée</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Classe</TableHead>
                        <TableHead>Niveau</TableHead>
                        <TableHead>Professeur</TableHead>
                        <TableHead className="text-center">Élèves</TableHead>
                        <TableHead className="text-center">Capacité</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {classes.map((cls) => {
                        const teacher = getTeacherForClass(cls.teacher_id);
                        const classStudents = getStudentsForClass(cls.id);
                        const level = levels.find(l => l.id === cls.level_id);
                        
                        return (
                          <TableRow key={cls.id}>
                            <TableCell className="font-medium">{cls.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{level?.name || 'N/A'}</Badge>
                            </TableCell>
                            <TableCell>
                              {teacher ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <User className="w-4 h-4 text-primary" />
                                  </div>
                                  <span>{teacher.first_name} {teacher.last_name}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Non assigné</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={classStudents.length > 0 ? "default" : "secondary"}>
                                {classStudents.length}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">{cls.capacity}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditClass(cls)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteClass(cls.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : levels.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground mb-4">Aucun niveau créé</p>
                <div className="flex justify-center gap-2">
                  <Button variant="outline" className="rounded-full" onClick={() => setShowLevelDialog(true)}>Créer manuellement</Button>
                  <Button className="rounded-full" onClick={() => seedLevels(selectedType)} disabled={formLoading}>
                    {formLoading ? 'Création...' : `Créer les niveaux ${selectedType === 'quranic' ? 'coraniques' : 'classiques'}`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {levels.map((level) => {
                const levelClasses = getClassesForLevel(level.id);
                return (
                  <Card key={level.id} className="card-hover" data-testid={`level-${level.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", 
                            selectedType === 'quranic' ? 'bg-primary/10' : 'bg-secondary/10')}>
                            {selectedType === 'quranic' ? 
                              <BookOpen className="w-5 h-5 text-primary" /> : 
                              <GraduationCap className="w-5 h-5 text-secondary" />}
                          </div>
                          <div>
                            <h3 className="font-semibold">{level.name}</h3>
                            <p className="text-sm text-muted-foreground">{level.description || 'Aucune description'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{levelClasses.length} classe(s)</Badge>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteLevel(level.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>

                      {levelClasses.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 pt-4 border-t">
                          {levelClasses.map((cls) => {
                            const teacher = getTeacherForClass(cls.teacher_id);
                            const classStudents = getStudentsForClass(cls.id);
                            return (
                              <div key={cls.id} className="p-3 bg-muted/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="font-medium">{cls.name}</p>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditClass(cls)}>
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteClass(cls.id)}>
                                      <Trash2 className="w-3 h-3 text-destructive" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    <span>{classStudents.length} élève(s)</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    <span>{teacher ? `${teacher.first_name} ${teacher.last_name}` : 'Pas de prof'}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
